import { useState, useEffect, useCallback } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import { AttributionData, EMPTY_ATTRIBUTION } from './types';

// The endpoint falls back to its own 90-day window when `from` is missing, so
// "all time" is sent as a date before any data exists.
const ALL_TIME_FROM = '2020-01-01';

export function useAttribution(
  apiUrl: string,
  from: string | null = null,
  to: string | null = null,
): {
  data: AttributionData;
  loading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();

  const [data, setData] = useState<AttributionData>(EMPTY_ATTRIBUTION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);

  const refetch = useCallback(() => setTrigger((t) => t + 1), []);

  useEffect(() => {
    if (!isAuthenticated || accounts.length === 0) return;

    let isMounted = true;

    const fetchData = async () => {
      if (!apiUrl) {
        if (isMounted) { setError('API URL is not configured for this environment'); setLoading(false); }
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const tokenResponse = { accessToken: await acquireApiToken(instance, accounts[0]) };
        const headers = {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json',
        };

        const qs = new URLSearchParams({ from: from ?? ALL_TIME_FROM });
        if (to) qs.set('to', to);
        const res = await fetch(
          `${apiUrl}/admin/socials/attribution?${qs}`,
          { headers },
        );
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);

        if (isMounted) {
          setData(json);
          setError(null);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message ?? 'Failed to load attribution data');
          setData(EMPTY_ATTRIBUTION);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [isAuthenticated, instance, accounts, apiUrl, from, to, trigger]);

  return { data, loading, error, refetch };
}
